import type { LendingCollateralPositionMap } from "@stepfinance/lending";

import type { MarketsContextState } from "../../contexts/market";
import { APT_MINT } from "../../models/apricot/ids";
import type {
  LendingData,
  ReformattedCollateralizedPosition,
  ReformattedLPCollateralizedPosition,
  ReformattedYieldPosition,
} from "../../types/lending";
import { isLPYieldPositionData } from "../../types/lending";
import { getTokenByMint } from "../tokenInfo";

export const getCollateralPositionRows = (
  priceInUSD: MarketsContextState["priceInUSD"],
  collateralMap: LendingCollateralPositionMap,
  platform: string,
): { rows: ReformattedCollateralizedPosition[]; totalNetValue: number; poolYieldAPY?: number } => {
  let totalNetValue = 0;
  const rows = Object.entries(collateralMap)
    .filter(([, { display }]) => display)
    .map(([mint, { deposit, borrow }]) => {
      const token = getTokenByMint(mint);
      const deposited = deposit.amount;
      const borrowed = borrow.amount;
      const depositApy = (deposit.apy ?? 0) + (deposit.rewardAPY?.reduce((a, b) => (a += b.apy ?? 0), 0) ?? 0);
      const borrowApy = (borrow.apy ?? 0) - (borrow.rewardAPY?.reduce((a, b) => (a += b.apy ?? 0), 0) ?? 0);
      const net = deposited - borrowed;
      let asset: string | [string, string] = mint;
      // TODO: Properly weight netApy
      let netApy: number | undefined = undefined;
      if (typeof depositApy === "number" || typeof borrowApy === "number") {
        netApy = (deposited && depositApy) - (borrowed && borrowApy);
      }
      if (token || isLPYieldPositionData(deposit)) {
        const tokenSymbol: string = token?.symbol ? token.symbol : "";
        const tokenPrice: number = priceInUSD(mint) ?? 0;
        let newRow: ReformattedCollateralizedPosition | ReformattedLPCollateralizedPosition = {
          key: `${platform}-${mint}`,
          platform,
          asset,
          tokenSymbol,
          tokenPrice,
          deposited,
          depositApy,
          borrowed,
          borrowApy: borrowApy !== undefined ? borrowApy * -1 : undefined,
          net,
          netApy,
        };
        if (isLPYieldPositionData(deposit)) {
          const tokenA = getTokenByMint(deposit.mintA);
          const tokenB = getTokenByMint(deposit.mintB);

          asset = [deposit.mintA, deposit.mintB];
          newRow = {
            ...newRow,
            asset,
            tokenPrice: deposit.tokenPrice,
            tokenSymbol: [tokenA?.symbol, tokenB?.symbol],
            lpDescription: "LP Tokens",
          } as ReformattedLPCollateralizedPosition;
        }
        if (deposit) {
          const amountUsdValue: number = isLPYieldPositionData(deposit)
            ? deposit.amount * (deposit.tokenPrice ?? 0)
            : deposit.amount * tokenPrice;
          totalNetValue = totalNetValue + amountUsdValue;
        }
        if (borrow) {
          const amountUsdValue: number = borrow.amount * tokenPrice;
          totalNetValue = totalNetValue - amountUsdValue;
        }
        return newRow;
      }
      return undefined;
    })
    .filter((row): row is ReformattedCollateralizedPosition => row !== undefined);

  return {
    rows,
    totalNetValue,
  };
};

export const formatRows = (
  platform: string,
  lendingData: LendingData[keyof LendingData],
  priceInUSD: MarketsContextState["priceInUSD"],
) => {
  let platformTotalNetValue = 0;
  let platformTotalNetLeveragedYieldValue = 0;
  let platformTotalNetCollateralValue = 0;
  let platformTotalWeightedYield = 0;
  let platformTotalWeight = 0;

  const isolatedPools: { [poolName: string]: ReturnType<typeof getCollateralPositionRows> } = {};
  const isolatedVaults: {
    [vaultName: string]: ReturnType<typeof getCollateralPositionRows> & { vaultPlatform: string; vaultName: string };
  } = {};
  const { rows, totalNetValue: totalCollateralNetValue } = getCollateralPositionRows(
    priceInUSD,
    lendingData.collateral,
    platform,
  );
  // Calculating total yield after 1-year using APY
  rows.forEach(({ deposited, depositApy, borrowed, borrowApy, tokenPrice }) => {
    const usedDepositApy = depositApy ?? 0;
    const usedBorrowApy = borrowApy ?? 0;
    const depositedUSD = deposited * tokenPrice;
    const borrowedUSD = borrowed * tokenPrice;
    platformTotalWeightedYield += depositedUSD * usedDepositApy;
    platformTotalWeightedYield += borrowedUSD * usedBorrowApy;
    platformTotalWeight += borrowedUSD + depositedUSD;
  });
  platformTotalNetValue += totalCollateralNetValue;
  platformTotalNetCollateralValue = totalCollateralNetValue;

  if (platform === "Solend" || platform === "Larix") {
    const pools = (lendingData as LendingData["solend"] | LendingData["larix"]).isolatedPools;
    Object.entries(pools).forEach(([poolName, collateralPositionMap]) => {
      const { rows, totalNetValue } = getCollateralPositionRows(priceInUSD, collateralPositionMap, platform);
      let poolTotalWeightedYield = 0;
      let poolTotalWeight = 0;
      rows.forEach(({ deposited, depositApy, borrowed, borrowApy, tokenPrice }) => {
        const usedDepositApy = depositApy ?? 0;
        const usedBorrowApy = borrowApy ?? 0;
        const depositedUSD = deposited * tokenPrice;
        const borrowedUSD = borrowed * tokenPrice;
        poolTotalWeightedYield += depositedUSD * usedDepositApy;
        poolTotalWeightedYield += borrowedUSD * usedBorrowApy;
        poolTotalWeight += depositedUSD + borrowedUSD;
      });
      platformTotalNetValue += totalNetValue;
      if (rows.length > 0) {
        isolatedPools[poolName] = {
          rows,
          totalNetValue,
          poolYieldAPY: poolTotalWeightedYield / poolTotalWeight,
        };
      }
    });
  }

  if (platform === "Ratio" || platform === "Hedge") {
    const vaults = (lendingData as LendingData["ratio"] | LendingData["hedge"]).isolatedVaults;
    Object.entries(vaults).forEach(([vaultName, vaultData]) => {
      const { rows, totalNetValue } = getCollateralPositionRows(priceInUSD, vaultData.collateralMap, platform);
      let vaultTotalWeightedYield = 0;
      let vaultTotalWeight = 0;
      rows.forEach(({ deposited, depositApy, borrowed, borrowApy, tokenPrice }) => {
        const usedDepositApy = depositApy ?? 0;
        const usedBorrowApy = borrowApy ?? 0;
        const depositedUSD = deposited * tokenPrice;
        const borrowedUSD = borrowed * tokenPrice;
        vaultTotalWeightedYield += depositedUSD * usedDepositApy;
        vaultTotalWeightedYield += borrowedUSD * tokenPrice * usedBorrowApy;
        vaultTotalWeight += borrowedUSD + depositedUSD;
      });
      platformTotalNetValue += totalNetValue;
      if (rows.length > 0) {
        isolatedVaults[vaultName] = {
          rows,
          totalNetValue,
          poolYieldAPY: vaultTotalWeightedYield / vaultTotalWeight,
          vaultPlatform: vaultData.vaultPlatform || "",
          vaultName: vaultData.vaultName || "",
        };
      }
    });
  }

  const yieldRows = Object.entries(lendingData.yieldFarms)
    .map(([lpMint, yieldData]) => {
      const {
        debtTokenAmountA,
        debtTokenAmountB,
        debtTotalValueUSD,
        mintA,
        mintB,
        equityUSD,
        positionAmountA,
        positionAmountB,
        positionValueUSD,
        killBuffer,
      } = yieldData;
      const tokenA = getTokenByMint(mintA);
      const tokenB = getTokenByMint(mintB);
      if (tokenA && tokenB) {
        platformTotalNetLeveragedYieldValue += positionValueUSD ?? 0;
        return {
          platform,
          key: lpMint,
          vault: [tokenA.address, tokenB.address],
          tokenSymbol: [tokenA.symbol, tokenB.symbol],
          debtTokenAmountA,
          debtTokenAmountB,
          debtTotalValueUSD,
          equityUSD,
          positionAmountA,
          positionAmountB,
          positionValueUSD,
          killBuffer,
        };
      }
      return undefined;
    })
    .filter((row): row is ReformattedYieldPosition => row !== undefined);

  const totalCollateralAPY = platformTotalWeightedYield / platformTotalWeight;

  return {
    rows,
    yieldRows,
    isolatedPools,
    isolatedVaults,
    platformTotalNetValue,
    platformTotalNetLeveragedYieldValue,
    platformTotalNetCollateralValue,
    totalCollateralAPY,
  };
};

export const getSupplyBorrowValue = (
  platform: string,
  collateral: LendingCollateralPositionMap,
  priceInUSD: MarketsContextState["priceInUSD"],
) => {
  let valueOfSupplied = 0;
  let valueOfBorrowed = 0;
  for (const [mint, { deposit, borrow }] of Object.entries(collateral)) {
    if (deposit && !(platform.toLowerCase() === "apricot" && mint === APT_MINT.toBase58())) {
      valueOfSupplied = isLPYieldPositionData(deposit)
        ? valueOfSupplied + deposit.amount * (deposit.tokenPrice ?? 0)
        : valueOfSupplied + deposit.amount * (priceInUSD(mint) ?? 0);
    }
    if (borrow) {
      valueOfBorrowed = valueOfBorrowed + borrow.amount * (priceInUSD(mint) ?? 0);
    }
  }
  return [valueOfSupplied, valueOfBorrowed];
};
